<template>
  <div id="volunteer-container">
    <v-form ref="form" class="volunteer-opp-filter-form margin-bottom">
      <v-layout row wrap>
        <v-flex xs12 sm5>
          <!-- // Category filter -->
          <v-select
            v-model="filter_location_choice"
            :items="filter_location_select"
            label="Select your location"
          >
            <template slot="selection" slot-scope="{ item }">{{ item.name }}</template>
            <template slot="item" slot-scope="{ item }">{{ item.name }}</template>
          </v-select>
        </v-flex>
        <v-flex xs12 sm5>
          <!-- // Location filter -->
          <v-select
            v-model="filter_category_choice"
            :items="filter_category_select"
            label="Area of Interest"
          >
            <template slot="selection" slot-scope="{ item }">{{ item.name }}</template>
            <template slot="item" slot-scope="{ item }">{{ item.name }}</template>
          </v-select>
        </v-flex>
        <v-flex xs12 sm2>
          <!-- // Submit button. -->
          <v-btn
            class="button-action button-action-green"
            v-on:click="updateVolunteerOpportunities(filter_location_choice, filter_category_choice)"
          >Filter</v-btn>
        </v-flex>
      </v-layout>
    </v-form>
    <p v-if="isOpportunityListEmpty" class="align-center">
      We&rsquo;re sorry, but there currently aren&rsquo;t any opportunities based on your selection.
      <br />Please select a different location and/or interest.
    </p>
    <!-- // Featured opportunities -->
    <v-sheet class="featured-opportunities margin-bottom">
      <carousel
        :paginationEnabled="false"
        :navigationEnabled="true"
        :perPageCustom="[[0,1], [600, 2], [970, 3]]"
      >
        <slide v-for="(volunteer_opp,i) in featured_volunteer_opportunities" :key="i">
          <v-card :ref="volunteer_opp.id" @click="scrollMeTo(volunteer_opp.id)" hover>
            <div
              v-if="volunteer_opp.featured_image"
              class="v-card__image-container"
              :style="{ backgroundImage: 'url(\'' + volunteer_opp.featured_image + '\')' }"
            >
              <v-chip color="yellow" text-color="green">
                <v-icon left>star_border</v-icon>Featured
              </v-chip>
            </div>
            <div
              v-else
              class="v-card__image-container"
              :style="{ backgroundImage: 'url(\'' + 'https://storage.googleapis.com/assets.festfoods.com/images/about/community-involvement/volunteers-default-img.jpg' + '\')' }"
            >
              <v-chip color="yellow" text-color="green">
                <v-icon left>star_border</v-icon>Featured
              </v-chip>
            </div>
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0" v-html="volunteer_opp.title.rendered"></h3>
                <div v-if="locationsGreaterThanOne(volunteer_opp)">Multiple Locations</div>
                <div v-else>
                  <template v-if="volunteer_opp.location">
                    {{ volunteer_opp.location }}
                    <br />
                  </template>
                  {{ volunteer_opp.locations[0].city }}, {{ volunteer_opp.locations[0].state }}
                </div>
              </div>
            </v-card-title>
          </v-card>
        </slide>
      </carousel>
    </v-sheet>
    <div id="12"></div>
    <!-- // Volunteer opportunities list -->
    <v-expansion-panel
      id="volunteer-opportunities-list"
      v-model="panel"
      class="volunteer-opportunities-list"
      value="3"
    >
      <v-expansion-panel-content
        v-for="(volunteer_opp, index) in volunteer_opportunities"
        :key="volunteer_opp.id"
        class="volunteer-opp"
      >
        <template v-slot:actions>
          <v-chip v-if="panel === index">
            Collapse
            <v-icon right>clear</v-icon>
          </v-chip>
          <v-chip v-else>
            More Info
            <v-icon right>add</v-icon>
          </v-chip>
        </template>
        <template v-slot:header>
          <div :id="'opp-' + volunteer_opp.id">
            <span class="title" v-html="volunteer_opp.title.rendered"></span>
            <span
              v-if="locationsGreaterThanOne(volunteer_opp)"
              class="sub-header"
            >Multiple Locations</span>
            <span
              v-else
              class="sub-header"
            >{{ volunteer_opp.locations[0].city }}, {{ volunteer_opp.locations[0].state }}</span>
          </div>
        </template>
        <v-card>
          <v-layout row wrap>
            <!-- // Grey block content -->
            <v-flex xs12 md5 offset-md1 order-md2>
              <div class="grey-block-graphic">
                <v-chip v-if="volunteer_opp.featured_opp === '1'" color="yellow" text-color="green">
                  <v-icon left>star_border</v-icon>Featured
                </v-chip>
                <img
                  v-if="volunteer_opp.featured_image"
                  :src="volunteer_opp.featured_image"
                  alt="volunteer_opp.title.rendered"
                />
                <img
                  v-else
                  src="https://storage.googleapis.com/assets.festfoods.com/images/about/community-involvement/volunteers-default-img.jpg"
                  alt="Festival wave"
                />
              </div>
              <div class="grey-block">
                <h2>Where</h2>
                <template v-for="(location,i) in volunteer_opp.locations">
                  <ul :key="i">
                    <li>
                      <template v-if="location.location">
                        {{ location.location }}
                        <br />
                      </template>
                      {{ location.address }}
                      <br />
                      {{ location.city }}, {{ location.state }} {{ location.zip }}
                    </li>
                  </ul>
                </template>
                <h2>When</h2>
                <p v-if="volunteer_opp.one_time_opp === '1'">{{ oppDate(volunteer_opp) }}</p>
                <p v-else>{{ volunteer_opp.flexible_frequency }}</p>
                <h2>Volunteers Needed</h2>
                <p
                  v-if="volunteer_opp.has_volunteer_limit === '0'"
                >See opportunity listing for details</p>
                <p v-else>{{ volunteer_opp.volunteer_limit }}</p>
                <a
                  class="button-action button-action-green"
                  v-bind:href="volunteer_opp.registration_link"
                  @click="newWindow($event)"
                >Sign Up</a>
              </div>
            </v-flex>
            <!-- // Left column details -->
            <v-flex xs12 md6>
              <h2 v-if="volunteer_opp.organization_name">Hosted by</h2>
              <p v-html="volunteer_opp.organization_name"></p>
              <h2 v-if="volunteer_opp.content.rendered">Description</h2>
              <div v-html="volunteer_opp.content.rendered"></div>
              <v-chip
                v-for="(opportunity_category,index) in volunteer_opp.opportunity_category"
                v-bind:key="index"
                color="#eef1e9"
                text-color="black"
              >{{ getVolunteerCategoryName(opportunity_category) }}</v-chip>
              <h2
                v-if="volunteer_opp.contact_full_name || volunteer_opp.contact_title || volunteer_opp.contact_phone || volunteer_opp.contact_email"
              >Contact Organizer</h2>
              <v-list two-line>
                <!-- // Name and title -->
                <v-list-tile v-if="volunteer_opp.contact_full_name || volunteer_opp.contact_title">
                  <v-list-tile-action>
                    <v-icon>person</v-icon>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title
                      v-if="volunteer_opp.contact_full_name"
                    >{{ volunteer_opp.contact_full_name }}</v-list-tile-title>
                    <v-list-tile-sub-title
                      v-if="volunteer_opp.contact_title"
                    >{{ volunteer_opp.contact_title }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <!-- // Phone -->
                <v-list-tile v-if="volunteer_opp.contact_phone">
                  <v-list-tile-action>
                    <v-icon>smartphone</v-icon>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ volunteer_opp.contact_phone }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
                <!-- // Email -->
                <v-list-tile v-if="volunteer_opp.contact_email">
                  <v-list-tile-action>
                    <v-icon>email</v-icon>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <a
                        :href="`mailto:${volunteer_opp.contact_email}`"
                      >{{ volunteer_opp.contact_email }}</a>
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
              <!-- // Share Listing -->
              <h2>Share Listing</h2>
              <div>
                <a
                  class="icon-facebook v-btn v-btn--flat v-btn--icon theme--light"
                  :href="`https://www.facebook.com/sharer.php?u=http://www.festfoods.com/about/volunteer#${volunteer_opp.id}`"
                  @click="newWindow($event)"
                >
                  <span class="accessibly-hidden">Facebook</span>
                </a>
                <a
                  class="icon-twitter v-btn v-btn--flat v-btn--icon theme--light"
                  :href="`https://twitter.com/intent/tweet?text=&url=http://www.festfoods.com/about/volunteer#${volunteer_opp.id}+&amp;text=${volunteer_opp.title.rendered}&amp;hashtags=volunteer`"
                  @click="newWindow($event)"
                >
                  <span class="accessibly-hidden">Twitter</span>
                </a>
              </div>
            </v-flex>
          </v-layout>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'volunteer-opportunities-list',
  props: {
    id: {
      type: String,
      required: false
    }
  },
  data: function () {
    return {
      next:
        '<div class="icon-arrow-right"><span class="accessibly-hidden">Next</span></div>',
      prev:
        '<div class="icon-arrow-left"><span class="accessibly-hidden">Previous</span></div>',
      panel: null,
      featured_volunteer_opportunities: '',
      volunteer_opportunities: '',
      volunteer_opportunity_categories: [],
      volunteer_opportunity_locations: [],
      filter_location_choice: '',
      filter_category_choice: '',
      filter_location_select: [{ value: null, name: 'All' }],
      filter_category_select: [{ value: null, name: 'All' }],
      error: '',
      rowsPerPageItems: [4, 8, 12],
      pagination: {
        rowsPerPage: 4
      }
    }
  },
  computed: {
    isOpportunityListEmpty: function () {
      if (this.volunteer_opportunities.length === 0) {
        return true
      } else {
        return false
      }
    }
  },
  methods: {
    getMonthName (monthId) {
      var months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]

      return months[monthId - 1]
    },
    oppDate (volunteerOpp) {
      var startDate = new Date(volunteerOpp.start_date * 1000 + 3600000 * 5)
      var start = [
        startDate.getUTCMonth() + 1, // Month
        startDate.getUTCDate(), // Day
        startDate.getUTCFullYear(), // Year
        startDate.getHours() > 12
          ? startDate.getHours() - 12
          : startDate.getHours(), // Hours
        startDate.getMinutes() < 10
          ? '0' + startDate.getMinutes()
          : startDate.getMinutes(), // Minutes
        startDate.getHours() >= 12 ? 'PM' : 'AM'
      ]
      var endDate = new Date(volunteerOpp.end_date * 1000 + 3600000 * 5)
      var end = [
        endDate.getUTCMonth() + 1, // Month
        endDate.getUTCDate(), // Day
        endDate.getUTCFullYear(), // Year
        endDate.getHours() > 12 ? endDate.getHours() - 12 : endDate.getHours(), // Hours
        endDate.getMinutes() < 10
          ? '0' + endDate.getMinutes()
          : endDate.getMinutes(), // Minutes
        endDate.getHours() >= 12 ? 'PM' : 'AM'
      ]
      var displayDate = null
      if (
        start[0] === end[0] &&
        start[1] === end[1] &&
        start[2] === end[2] &&
        start[3] === end[3] &&
        start[4] === end[4] &&
        start[5] === end[5]
      ) {
        displayDate =
          this.getMonthName(start[0]) +
          ' ' +
          start[1] +
          ', ' +
          start[2] +
          ' @ ' +
          start[3] +
          ':' +
          start[4] +
          ' ' +
          start[5]
      } else {
        displayDate =
          this.getMonthName(start[0]) +
          ' ' +
          start[1] +
          ', ' +
          start[2] +
          ' @ ' +
          start[3] +
          ':' +
          start[4] +
          ' ' +
          start[5] +
          ' - ' +
          this.getMonthName(end[0]) +
          ' ' +
          end[1] +
          ', ' +
          end[2] +
          ' @ ' +
          end[3] +
          ':' +
          end[4] +
          ' ' +
          end[5]
      }

      return displayDate
    },
    getVolunteerCategoryName (id) {
      var category = ''

      category = this.volunteer_opportunity_categories.filter(
        volunteerOpp => volunteerOpp.id === id
      )

      return category[0].name
    },
    get_featured_volunteer_opportunities (location, category) {
      var data = {
        method: 'get',
        url:
          'https://volunteer.festfoods.com/wp-json/wp/v2/volunteer_opp?meta_key=featured_opp&meta_value=1&per_page=100',
        // url: 'http://localhost:8080/wp-json/wp/v2/volunteer_opp?meta_key=featured_opp&meta_value=1',
        params: {}
      }
      if (location) {
        data.params.opportunity_location = location
      }
      if (category) {
        data.params.opportunity_category = category
      }
      axios(data)
        .then(response => {
          var currentDate = new Date()
          var currentEpoch = Math.round(currentDate.getTime() / 1000)
          var data = response.data
          var filteredData = []
          var length = response.data.length

          for (var i = 0; i < length; i++) {
            if (
              currentEpoch < data[i].end_date ||
              data[i].one_time_opp === '0'
            ) {
              filteredData.push(data[i])
            }
          }
          this.featured_volunteer_opportunities = filteredData
        })
        .catch(function (error) {
          this.error = error.data
        })
    },
    get_volunteer_opportunities (location, category) {
      var data = {
        method: 'get',
        url:
          'https://volunteer.festfoods.com/wp-json/wp/v2/volunteer_opp?per_page=100',
        // url: 'http://localhost:8080/wp-json/wp/v2/volunteer_opp',
        params: {}
      }
      if (location) {
        data.params.opportunity_location = location
      }
      if (category) {
        data.params.opportunity_category = category
      }
      axios(data)
        .then(response => {
          var currentDate = new Date()
          var currentEpoch = Math.round(currentDate.getTime() / 1000)
          var data = response.data
          var filteredData = []
          var length = response.data.length

          for (var i = 0; i < length; i++) {
            if (
              currentEpoch < data[i].end_date ||
              data[i].one_time_opp === '0'
            ) {
              filteredData.push(data[i])
            }
          }
          this.volunteer_opportunities = filteredData
          if (!this.isOpportunityListEmpty) {
            this.checkAndSetOpenPanel()
          }
        })
        .catch(function (error) {
          this.error = error.data
        })
    },
    get_volunteer_opportunity_categories () {
      axios({
        method: 'get',
        url:
          'https://volunteer.festfoods.com/wp-json/wp/v2/opportunity_category?per_page=100'
      })
        .then(response => {
          // Store our category taxonomy options.
          this.volunteer_opportunity_categories = response.data

          for (
            var i = 0;
            i < this.volunteer_opportunity_categories.length;
            i++
          ) {
            this.filter_category_select.push({
              value: this.volunteer_opportunity_categories[i].id,
              name: this.volunteer_opportunity_categories[i].name
            })
          }
        })
        .catch(function (error) {
          this.error = error.data
        })
    },
    get_volunteer_opportunity_locations () {
      axios({
        method: 'get',
        url:
          'https://volunteer.festfoods.com/wp-json/wp/v2/opportunity_location?per_page=100'
      })
        .then(response => {
          // Store our location taxonomy options.
          this.volunteer_opportunity_locations = response.data

          for (
            var i = 0;
            i < this.volunteer_opportunity_locations.length;
            i++
          ) {
            this.filter_location_select.push({
              value: this.volunteer_opportunity_locations[i].id,
              name: this.volunteer_opportunity_locations[i].name
            })
          }
        })
        .catch(function (error) {
          this.error = error.data
        })
    },
    locationsGreaterThanOne (opportunity) {
      var locationCount = opportunity.locations.length

      if (locationCount > 1) {
        return true
      } else {
        return false
      }
    },
    checkAndSetOpenPanel () {
      var url = window.location.href
      var hash = url.substring(url.indexOf('#') + 1)
      if (hash && (!hash.includes('http://') && !hash.includes('https://'))) {
        this.setOpenPanel(hash)
        this.scrollMeTo(hash)
      }
    },
    scrollMeTo (id) {
      this.setOpenPanel(id)
      var list = document.querySelector('#volunteer-opportunities-list')
      var listDistanceFromTop = this.getScrollOffset(list)
      window.scroll(0, listDistanceFromTop + this.panel * 67)
    },
    setOpenPanel (id) {
      var oppLength = this.volunteer_opportunities.length
      for (var i = 0; i < oppLength; i++) {
        if (this.volunteer_opportunities[i].id.toString() === id.toString()) {
          this.panel = i
        }
      }
    },
    updateVolunteerOpportunities (filterLocationChoice, filterCategoryChoice) {
      this.get_volunteer_opportunities(
        filterLocationChoice,
        filterCategoryChoice
      )
      this.get_featured_volunteer_opportunities(
        filterLocationChoice,
        filterCategoryChoice
      )
    }
  },
  created: function () {
    this.get_volunteer_opportunity_categories()
    this.get_volunteer_opportunity_locations()
    this.get_featured_volunteer_opportunities()
    this.get_volunteer_opportunities()
    this.checkAndSetOpenPanel()
  }
}
</script>

<style lang="scss">
#vol-opp-list {
  margin: 0 auto;
  width: 1000px;
}

@media (max-width: 1100px) {
  #vol-opp-list {
    width: 90%;
  }
}

/**
* Featured opportunities carousel
**/
.VueCarousel {
  .VueCarousel-wrapper {
    padding-bottom: 15px;
  }

  .VueCarousel-slide {
    .v-card {
      margin: 0 auto;
      max-width: 100%;
      width: 300px;
    }

    .v-chip {
      position: absolute;
      right: 10px;
      top: 10px;
    }

    .v-card__image-container {
      background-position: center center;
      background-size: cover;
      height: 400px;
      overflow: hidden;

      img {
        display: block;
        height: 400px;
        width: auto;
      }
    }

    .v-card__title {
      background-color: rgba(255, 255, 255, 0.85);
      bottom: 0;
      left: 0;
      position: absolute;
      width: 100%;
    }
  }
}

// Carousel nav
.VueCarousel-navigation-button {
  color: $green !important;
  font-size: 3rem;
  padding: 0 !important;

  .accessible & {
    color: $green-accessible !important;
  }
}

/**
* Buttons
**/
.button-action,
a.button-action {
  color: #fff;
  font-size: 1.5rem;
  text-decoration: none;
}

.button-action-green,
a.button-action-green {
  background-color: #3a823d;
}

a:hover.button-action-green {
  background-color: #416e3c;
}

/**
* Social share icons
**/
.icon-facebook,
.icon-twitter {
  border-radius: 120px;
  color: #ffffff;
  font-size: 23px;
  overflow: hidden;

  &::before {
    color: #ffffff;
    opacity: 1;
  }

  &.v-btn--active:before,
  &.v-btn:focus:before,
  &.v-btn:hover:before {
    background: transparent;
  }
}

.icon-facebook {
  background-color: #405994;

  &::before {
    content: "\e919";
    left: 7px;
    top: 6px;
  }

  &:hover,
  &.v-btn--active:before,
  &.v-btn:focus:before,
  &.v-btn:hover:before {
    background-color: #4a66aa;
  }
}

.icon-twitter {
  background-color: #4face4;

  &::before {
    content: "\e938";
    left: 8px;
    top: 6px;
  }

  &:hover,
  &.v-btn--active,
  &.v-btn:focus,
  &.v-btn:hover {
    background-color: #71caff;
  }
}

/**
* Spacing
**/
.margin-bottom {
  margin-bottom: 50px;
}
#volunteer-container {
  .application {
    background: transparent;
  }

  /**
  * Filter Form
  **/
  .volunteer-opp-filter-form {
    .v-input__slot {
      background-color: #f5f5f5;
      padding: 9px;
    }

    .v-input__append-inner {
      background-color: #3a823d;
      padding-left: 0;
    }

    i.v-icon.material-icons.theme--light.primary--text {
      color: #ffffff !important;
    }

    .theme--light.v-icon {
      color: #ffffff;
    }

    .flex:first-child {
      padding-right: 1%;

      @media (max-width: 700px) {
        padding-left: 0;
        padding-right: 0;
        width: 100%;
      }
    }

    .flex:nth-child(2) {
      padding-left: 1%;
      padding-right: 1%;

      @media (max-width: 700px) {
        padding-left: 0;
        padding-right: 0;
        width: 100%;
      }
    }

    .button-action {
      font-weight: bold;
      height: 51px !important;
      margin-top: 16px;
      width: 100%;

      @media (max-width: 700px) {
        margin: 0;
      }
    }
  }

  .theme--light.v-btn:not(.v-btn--icon):not(.v-btn--flat) {
    background-color: #3a823d;
    border-radius: 0;
    box-shadow: none;
    color: #ffffff;
    height: 48px;
  }

  .theme--light.v-btn:not(.v-btn--icon):not(.v-btn--flat):hover {
    background-color: #416e3c;
  }

  /**
  * Featured Opportunity
  **/
  @media (max-width: 700px) {
    .featured-opportunities {
      margin: 0 auto;
      width: 305px;
    }
  }

  /**
  * List
  **/
  .v-expansion-panel .volunteer-opp .v-expansion-panel__header span {
    font-size: 1.4rem;
  }

  .v-expansion-panel
    .volunteer-opp
    .v-expansion-panel__header
    .v-chip__content {
    background-color: #3a823d;
    color: #ffffff;
    font-weight: bold;

    i.v-icon.v-icon--right.material-icons.theme--light {
      color: #ffffff;
    }
  }

  .v-expansion-panel
    .v-expansion-panel__container--active
    .v-expansion-panel__header
    .v-chip__content {
    background-color: #ffffff;
    color: #3a823d;

    i.v-icon.v-icon--right.material-icons.theme--light {
      color: #3a823d;
    }
  }

  .v-expansion-panel {
    box-shadow: none;

    .v-expansion-panel__header {
      padding: 20px 24px 20px 0;

      .title {
        font-size: 2.4rem !important;
      }

      @media (max-width: 740px) {
        .title {
          display: block;
          margin-bottom: 0.4rem;
          width: 100%;
        }

        .sub-header {
          display: block;
          margin-left: 0 !important;
          width: 100%;
        }
      }
    }

    .v-expansion-panel__body {
      padding-bottom: 40px;
    }
  }

  .volunteer-opportunities-list {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    border-top: 1px solid rgba(0, 0, 0, 0.12);

    .v-list--two-line .v-list__tile {
      height: 50px;
    }
  }

  // Volunteer opportunity
  .volunteer-opp {
    .v-expansion-panel__header {
      font-size: 1.3rem;
      font-weight: bold;
    }

    .v-expansion-panel__header span,
    .v-expansion-panel__header .sub-header {
      font-size: 1rem;
      font-weight: normal;
      margin-left: 0;
    }

    .v-expansion-panel__header .sub-header {
      font-style: italic;
      margin-left: 10px;
    }

    h2 {
      font-size: 1.4rem;
      margin-bottom: 4px;
      margin-top: 30px;
      text-transform: uppercase;

      &:first-child {
        margin-top: 0;
      }
    }

    .flex {
      display: block;
    }
  }

  // Grey block
  .grey-block-graphic {
    overflow: hidden;
    position: relative;

    .v-chip {
      position: absolute;
      right: 7px;
      top: 8px;
    }

    img {
      display: block;
      max-width: 100%;
      width: 100%;
    }
  }

  .grey-block {
    background-color: #e9e7e8;
    margin-bottom: 25px;
    padding: 30px 40px;

    p {
      margin-bottom: 0;
    }

    .button-action {
      display: inline-block;
      font-size: 1.5rem;
      margin-top: 30px;
      padding: 14px 0;
      text-align: center;
      text-transform: uppercase;
      width: 100%;
    }
  }

  /**
  * Submission form
  **/
  .volunteer-opp-submission-form {
    // Headings
    h2 {
      font-size: 1.1rem;
      margin-bottom: 20px;
      margin-top: 30px;
      text-transform: uppercase;
    }

    h3 {
      color: #919191;
      font-size: 0.95rem;
      margin-bottom: 20px;
      text-transform: uppercase;
    }

    h4 {
      color: #919191;
      font-size: 0.9rem;
    }

    // Inputs
    .v-input {
      padding-top: 0;
    }

    .v-input__slot {
      background-color: #f5f5f5;
      padding: 10px;
    }

    .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
      border-color: #f5f5f5;
    }
    .calendar-selection {
      .input-container {
        background-color: #f5f5f5;
        display: inline-block;
        margin-bottom: 20px;
        padding-left: 5%;
        width: 95%;

        @media (max-width: 800px) {
          padding-left: 3%;
          width: 100%;
        }
      }

      .input-container .v-input__slot {
        padding: 0;
        margin-bottom: 0;
      }

      .v-input {
        display: inline-block;
        margin: 0 3%;
        width: 18%;

        &:first-child {
          margin-left: 0;
          width: 36%;
        }

        &:last-child {
          margin-right: 0;
          width: 30%;
        }

        .v-input__slot {
          background-color: transparent;
        }
      }

      .cal-icon {
        position: relative;
        top: 10px;
      }

      &.second {
        h4 {
          margin-left: 5%;
        }

        .input-container {
          margin-left: 5%;
        }
      }
    }

    .placeholder-item {
      background-color: #f5f5f5;
      text-align: center;
      padding: 33%;
      width: 100%;
    }

    .v-btn {
      padding-left: 0;
      padding-right: 0;
      margin-left: 0;
      margin-right: 0;
      width: 100%;
    }

    #featured-image-placeholder {
      border: 1.75px dashed #cccccc;
      margin-bottom: 20px;
      padding: 5px;

      img {
        display: block;
        height: auto;
        width: 100%;
      }
    }
  }
}
</style>
